// Función para formatear el número de comitente para visualización
export const formatComitenteForDisplay = (nro) => {
  // Solo se formatea el comitente local de 9 dígitos (568 + 6). Las cuentas
  // Jefferies (alfanuméricas, ej. "JW3165891") se devuelven sin tocar.
  if (!nro || typeof nro !== 'string' || !/^\d{9}$/.test(nro)) {
    return nro;
  }
  return `${nro.substring(0, 3)} ${nro.substring(3)}`;
};

/** Deja solo los dígitos de un CUIT/CUIL, para comparar y guardar sin formato. */
export const parseCuitDigits = (value) => String(value ?? '').replace(/\D/g, '');

/**
 * Parsea un número en formato argentino tal como viene en un archivo importado
 * ("1.234.567,89", "20,900", "102.946.702,000").
 *
 * A diferencia de `parseNumberForState`, que asume que el último separador es
 * siempre el decimal, acá se desambigua: con coma presente, la coma es el
 * decimal y los puntos son de miles; sin coma, un único punto seguido de
 * exactamente 3 dígitos también es de miles ("102.946" = 102946, no 102,946).
 * Es la diferencia entre leer bien y equivocarse por 1000x.
 *
 * @param {string|number} str
 * @param {boolean} allowNull - si true devuelve null en vez de 0 cuando no hay valor
 * @returns {number|null}
 */
export const parseArgentineNumber = (str, allowNull = false) => {
  if (str === '' || str === null || str === undefined) {
    return allowNull ? null : 0;
  }
  if (typeof str === 'number') {
    return isNaN(str) ? (allowNull ? null : 0) : str;
  }

  const cleaned = String(str).trim().replace(/^["']+|["']+$/g, '');
  if (cleaned === '' || cleaned === '-') {
    return allowNull ? null : 0;
  }

  const sanitized = cleaned.replace(/[^\d,.-]/g, '');
  if (sanitized === '' || sanitized === '-') {
    return allowNull ? null : 0;
  }

  let normalized = sanitized;
  const commaIndex = normalized.lastIndexOf(',');
  const lastDotIndex = normalized.lastIndexOf('.');

  if (commaIndex > -1) {
    // Hay coma: es el separador decimal, los puntos previos son de miles.
    const integerPart = normalized.substring(0, commaIndex).replace(/\./g, '');
    const decimalPart = normalized.substring(commaIndex + 1);
    normalized = `${integerPart}.${decimalPart}`;
  } else if (lastDotIndex > -1) {
    const dotsCount = (normalized.match(/\./g) || []).length;
    if (dotsCount > 1) {
      // Varios puntos: todos son de miles.
      normalized = normalized.replace(/\./g, '');
    } else {
      const afterDot = normalized.substring(lastDotIndex + 1);
      if (/^\d{3}$/.test(afterDot)) {
        // "102.946" -> separador de miles.
        normalized = normalized.replace('.', '');
      }
      // Con 1-2 dígitos después del punto se respeta como decimal internacional.
    }
  }

  const result = parseFloat(normalized);
  return isNaN(result) ? (allowNull ? null : 0) : result;
};

/**
 * Parsea las fechas que traen los archivos de operaciones de los mercados.
 * Acepta el formato argentino "DD/MM/AAAA" con hora opcional
 * ("30/07/2026 10:57:36") y el ISO sin zona ("2026-07-30T11:34:07").
 *
 * Siempre construye la fecha en hora local: los archivos vienen en hora de
 * Buenos Aires y un `new Date(iso)` la interpretaría como UTC, corriendo el día.
 *
 * @param {string|Date} value
 * @returns {Date|null} null si no se pudo interpretar
 */
export const parseFechaHoraArgentina = (value) => {
  if (!value) return null;
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value;

  const raw = String(value).trim();
  if (!raw) return null;

  const argentino = raw.match(
    /^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:[\sT]+(\d{1,2}):(\d{2})(?::(\d{2}))?)?$/
  );
  if (argentino) {
    const [, d, m, y, hh = '0', mm = '0', ss = '0'] = argentino;
    const fecha = new Date(
      Number(y), Number(m) - 1, Number(d), Number(hh), Number(mm), Number(ss)
    );
    return isNaN(fecha.getTime()) ? null : fecha;
  }

  const iso = raw.match(
    /^(\d{4})-(\d{2})-(\d{2})(?:[\sT]+(\d{1,2}):(\d{2})(?::(\d{2}))?)?/
  );
  if (iso) {
    const [, y, m, d, hh = '0', mm = '0', ss = '0'] = iso;
    const fecha = new Date(
      Number(y), Number(m) - 1, Number(d), Number(hh), Number(mm), Number(ss)
    );
    return isNaN(fecha.getTime()) ? null : fecha;
  }

  return null;
};

/**
 * Formatea un CUIT/CUIL como XX-XXXXXXXX-X. Si no tiene 11 dígitos se devuelve
 * lo tipeado tal cual, para no pelear con el usuario mientras escribe.
 */
export const formatCuitForDisplay = (value) => {
  const digits = parseCuitDigits(value);
  if (digits.length !== 11) return String(value ?? '').trim();
  return `${digits.substring(0, 2)}-${digits.substring(2, 10)}-${digits.substring(10)}`;
};

export const formatCurrency = (value, currency) => {
  const numberValue = parseFloat(value);
  if (isNaN(numberValue)) return '';
  const options = { style: 'currency', currency: currency === 'Pesos' ? 'ARS' : 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 };
  const numberFormat = new Intl.NumberFormat('es-AR', options);
  return numberFormat.format(numberValue).replace('ARS', '$').replace('USD', 'U$S');
};

export const formatPrice = (value, currency) => {
  const numberValue = parseFloat(value);
  if (isNaN(numberValue)) return '';
  const options = { style: 'currency', currency: currency === 'Pesos' ? 'ARS' : 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 };
  const numberFormat = new Intl.NumberFormat('es-AR', options);
  return numberFormat.format(numberValue).replace('ARS', '$').replace('USD', 'U$S');
};

export const formatPesos = (value) => {
  const numberValue = parseFloat(value);
  if (isNaN(numberValue)) return '';
  const options = { style: 'currency', currency: 'ARS', minimumFractionDigits: 2, maximumFractionDigits: 2 };
  const numberFormat = new Intl.NumberFormat('es-AR', options);
  return numberFormat.format(numberValue).replace('ARS', '$');
};

export const formatDolares = (value) => {
    const numberValue = parseFloat(value);
    if (isNaN(numberValue)) return '';
    const options = { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 };
    const numberFormat = new Intl.NumberFormat('en-US', options); // Use en-US for standard USD formatting
    return numberFormat.format(numberValue).replace('$', 'U$S ');
};

/*
 * Formatea un saldo en la moneda que corresponda. Acepta indistintamente el
 * bucket de cálculo ('ARS' | 'USD' | 'CABLE') y la etiqueta de movimiento
 * ('Pesos' | 'USD Mep' | 'USD Cable'), que es lo que circula por los
 * componentes según de dónde venga el dato.
 */
export const formatSaldo = (value, moneda) =>
  (moneda === 'ARS' || moneda === 'Pesos') ? formatPesos(value) : formatDolares(value);

export const formatNumberForDisplay = (value, digits = 2) => {
    if (value === null || value === undefined || value === '') return '';
    const numberValue = parseFloat(String(value).replace(',', '.'));
    if (isNaN(numberValue)) return '';
    return new Intl.NumberFormat('es-AR', { minimumFractionDigits: digits, maximumFractionDigits: digits }).format(numberValue);
};

// Formato plano para edición: usa "." como separador decimal y sin separador de miles.
// Permite escribir cómodamente con el pad numérico.
export const formatNumberForInput = (value) => {
    if (value === null || value === undefined || value === '') return '';
    const parsed = parseNumberForState(String(value));
    if (!parsed) return '';
    const numberValue = parseFloat(parsed);
    if (isNaN(numberValue)) return '';
    return parsed;
};

// Cantidades nominales (VN): siempre enteras, así que todo "." o "," es
// separador de miles. parseNumberForState no sirve acá porque interpreta el
// último separador como decimal y leería "50.000.000" como 50.000,000 — un
// error de escala de 1000x al releer un valor ya formateado para mostrar.
export const parseVnForState = (str) => {
    if (str === null || str === undefined) return '';
    return String(str).replace(/\D/g, '');
};

export const parseNumberForState = (str) => {
    if (typeof str !== 'string' || !str) return '';

    // Limpia caracteres no numéricos excepto separadores.
    const cleaned = str.replace(/[^\d.,]/g, '');
    if (!cleaned) return '';

    // El último separador (sea "." o ",") es el decimal. Los anteriores son de miles.
    const lastDot = cleaned.lastIndexOf('.');
    const lastComma = cleaned.lastIndexOf(',');
    const decimalIdx = Math.max(lastDot, lastComma);

    if (decimalIdx === -1) {
        return cleaned;
    }

    const integerPart = cleaned.slice(0, decimalIdx).replace(/[.,]/g, '');
    const decimalPart = cleaned.slice(decimalIdx + 1).replace(/[.,]/g, '');

    if (!decimalPart) {
        return integerPart || '0';
    }
    return `${integerPart || '0'}.${decimalPart}`;
};

// Cantidades y montos tipeados a mano. parseNumberForState lee "25.000" como
// 25 (toma el último "." como decimal) y una orden de 25.000 VN se guardaba por
// 25. Acá un único punto seguido de exactamente tres dígitos es de miles, como
// se escribe en es-AR, y la coma es siempre el decimal (parseArgentineNumber).
// El precio y la tasa NO pasan por acá: "98.125" es un precio base 100 con tres
// decimales tipeado con el pad numérico.
export const parseAmountForState = (str) => {
    if (typeof str !== 'string' || !str) return '';
    const cleaned = str.replace(/[^\d.,]/g, '');
    if (!cleaned) return '';
    return String(parseArgentineNumber(cleaned));
};

export const formatTasaForDisplay = (value) => {
    if (value === null || value === undefined || value === '') return '';
    const numberValue = parseFloat(String(value).replace(',', '.'));
    if (isNaN(numberValue)) return '';
    return `${new Intl.NumberFormat('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(numberValue)} %`;
};

export const parseTasaForState = (str) => {
    if (typeof str !== 'string' || !str) return '';
    return parseNumberForState(str.replace('%', '').trim());
};

/**
 * Formatea un número con separadores de miles para la localidad 'es-AR'.
 * @param {number | string} number El número a formatear.
 * @returns {string} El número formateado o un string vacío si la entrada no es válida.
 */
export const formatNumber = (number) => {
  if (number === null || number === undefined || number === '') {
    return '';
  }
  const num = Number(number);
  if (isNaN(num)) {
    return '';
  }
  return num.toLocaleString('es-AR');
};
